import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Box, CircularProgress, InputBase, LinearProgress, MenuItem, Pagination, TextField, Tooltip, Typography } from '@mui/material';
import { Link } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
    attractionsService,
    type AttractionBooking, type BookingsWhen, type Paged,
} from '../../services/attractions/attractionsService.ts';
import { LocationService } from '../../services/location/locationService.ts';
import { AttractionsFrame, COLORS, Panel, errorText, fmtAgo, fmtCount, fmtDateTime, fmtPlanDate } from './attractionsShared.tsx';

const PAGE_SIZE = 25;
const GRID = 'minmax(150px, .9fr) minmax(260px, 2fr) minmax(180px, 1.3fr) minmax(140px, .9fr) 130px';

const WHENS: { key: BookingsWhen; label: string }[] = [
    { key: 'upcoming', label: 'Upcoming' },
    { key: 'past', label: 'Past' },
    { key: 'all', label: 'All' },
];

const locationService = new LocationService();

type City = { id: number; name: string };

const countLine = (total: number, when: BookingsWhen, filtered: boolean): string => {
    const noun = total === 1 ? 'booking' : 'bookings';
    const what = when === 'upcoming' ? `upcoming ${noun}` : when === 'past' ? `past ${noun}` : noun;
    return `${fmtCount(total)} ${what}${filtered ? ' match the filters' : ''}`;
};

/** The customer's name, linked to their page in Members (where their credit is too). */
export const CustomerCell: React.FC<{ b: AttractionBooking }> = ({ b }) => (
    <Box sx={{ minWidth: 0 }}>
        <Box component={Link} to={`/members/${b.customerId}`}
            sx={{ fontSize: 13.5, fontWeight: 700, color: COLORS.text, textDecoration: 'none', overflowWrap: 'anywhere', '&:hover': { color: COLORS.tealDark, textDecoration: 'underline' } }}>
            {b.customerName?.trim() || b.customerEmail || `Customer #${b.customerId}`}
        </Box>
        {b.customerEmail && b.customerName?.trim() && (
            <Typography sx={{ fontSize: 12, color: COLORS.muted, mt: '2px', overflowWrap: 'anywhere' }}>{b.customerEmail}</Typography>
        )}
    </Box>
);

/** The product code, opening the product on Viator in a new tab when the link is known. */
export const ViatorLink: React.FC<{ b: AttractionBooking }> = ({ b }) => (b.viatorUrl ? (
    <Box component="a" href={b.viatorUrl} target="_blank" rel="noopener noreferrer" title="Open on Viator"
        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontSize: 12.5, fontWeight: 500, color: COLORS.tealDark, textDecoration: 'none',
            fontVariantNumeric: 'tabular-nums', '&:hover': { textDecoration: 'underline' } }}>
        {b.productCode}<OpenInNewIcon sx={{ fontSize: 14 }} />
    </Box>
) : (
    <Typography sx={{ fontSize: 12.5, color: COLORS.muted }}>{b.productCode}</Typography>
));

const BookingRow: React.FC<{ b: AttractionBooking }> = ({ b }) => (
    <Box data-booking={b.id} sx={{
        display: { xs: 'flex', md: 'grid' }, flexDirection: 'column', gridTemplateColumns: GRID, gap: { xs: 1, md: 1.5 },
        px: 2.25, py: 1.5, alignItems: { md: 'center' },
        borderBottom: `1px solid ${COLORS.rowBorder}`, '&:last-of-type': { borderBottom: 0 }, '& > *': { minWidth: 0 },
    }}>
        <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: COLORS.text, fontVariantNumeric: 'tabular-nums' }}>
            {fmtPlanDate(b.planDate, b.startTime)}
            {!b.startTime && <Box component="span" sx={{ display: 'block', fontSize: 12, fontWeight: 400, color: COLORS.muted, mt: '2px' }}>no time given</Box>}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
            <Box sx={{ width: 56, height: 42, flex: '0 0 56px', borderRadius: '6px', bgcolor: '#D7DEDB', overflow: 'hidden' }}>
                {b.thumbnailUrl && (
                    <img src={b.thumbnailUrl} alt="" loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                        onError={e => { e.currentTarget.style.display = 'none'; }} />
                )}
            </Box>
            <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 14, fontWeight: 700, lineHeight: 1.25, color: COLORS.text, overflowWrap: 'anywhere' }}>{b.title}</Typography>
                <Typography sx={{ fontSize: 12, color: COLORS.muted, mt: '3px' }}>{b.cityName ?? '—'}</Typography>
            </Box>
        </Box>
        <CustomerCell b={b} />
        <Box>
            <Tooltip title={fmtDateTime(b.markedAt)} placement="top-start">
                <Typography component="span" sx={{ fontSize: 13, color: COLORS.body }}>
                    <Box component="span" sx={{ display: { md: 'none' }, color: COLORS.muted }}>Marked booked </Box>
                    {fmtAgo(b.markedAt)}
                </Typography>
            </Tooltip>
            <Typography sx={{ fontSize: 12, color: COLORS.muted, mt: '2px' }}>{fmtDateTime(b.markedAt)}</Typography>
        </Box>
        <Box sx={{ textAlign: { md: 'end' } }}><ViatorLink b={b} /></Box>
    </Box>
);

const segmentSx = (on: boolean) => ({
    height: 34, borderRadius: '8px', px: 1.5, display: 'flex', alignItems: 'center', fontSize: 13, fontFamily: 'inherit', cursor: 'pointer',
    ...(on
        ? { bgcolor: COLORS.text, color: '#fff', fontWeight: 700, border: `1px solid ${COLORS.text}` }
        : { bgcolor: '#fff', color: COLORS.text, border: `1px solid ${COLORS.border}` }),
});

const fieldSx = { bgcolor: '#fff', '& .MuiOutlinedInput-root': { height: 36, borderRadius: '8px', fontSize: 13 }, '& .MuiInputLabel-root': { fontSize: 13 } };

/**
 * Attractions › Bookings: which attractions customers said they booked.
 * Customers book on Viator and answer "Did you book it?" in the app; this is
 * that answer. Viator's partner dashboard holds the confirmed bookings.
 */
const AttractionsBookingsPage = () => {
    const [when, setWhen] = useState<BookingsWhen>('upcoming');
    const [from, setFrom] = useState('');
    const [to, setTo] = useState('');
    const [cityId, setCityId] = useState<number | ''>('');
    const [q, setQ] = useState('');
    const [query, setQuery] = useState('');
    const [page, setPage] = useState(1);
    const [cities, setCities] = useState<City[]>([]);
    const [data, setData] = useState<Paged<AttractionBooking> | null>(null);
    const [loading, setLoading] = useState(false);
    const [failed, setFailed] = useState<string | null>(null);
    const requestId = useRef(0);

    // Search as you type, a beat after the last key.
    useEffect(() => {
        const t = setTimeout(() => setQuery(q), 350);
        return () => clearTimeout(t);
    }, [q]);

    // Any filter change goes back to the first page.
    useEffect(() => { setPage(1); }, [when, from, to, cityId, query]);

    useEffect(() => {
        locationService.getCities()
            .then(list => setCities((Array.isArray(list) ? list : []).map((c: any) => ({ id: c.id, name: c.name }))))
            .catch(() => setCities([]));
    }, []);

    const rangeBad = !!from && !!to && from > to;

    const load = useCallback(async () => {
        if (rangeBad) return;
        const id = ++requestId.current;
        setLoading(true);
        try {
            const start = (page - 1) * PAGE_SIZE;
            const next = await attractionsService.bookings({ when, from, to, cityId: cityId || null, q: query }, start, start + PAGE_SIZE);
            if (id !== requestId.current) return;
            setData(next);
            setFailed(null);
        } catch (e) {
            if (id !== requestId.current) return;
            setFailed(errorText(e, 'Could not load the bookings. Reload the page to try again.'));
        } finally {
            if (id === requestId.current) setLoading(false);
        }
    }, [when, from, to, cityId, query, page, rangeBad]);

    useEffect(() => { load(); }, [load]);

    const filtered = !!(from || to || cityId || query);
    const pages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;

    return (
        <AttractionsFrame
            title="Attraction bookings"
            subtitle="Customers mark these themselves after booking on Viator. Confirmed bookings and commission are in the Viator partner dashboard."
        >
            <Box data-bookings-filters="1" sx={{ display: 'flex', columnGap: 1, rowGap: 1.75, flexWrap: 'wrap', alignItems: 'center' }}>
                {WHENS.map(w => (
                    <Box key={w.key} component="button" type="button" onClick={() => setWhen(w.key)} aria-pressed={w.key === when} sx={segmentSx(w.key === when)}>
                        {w.label}
                    </Box>
                ))}
                <Box sx={{ display: { xs: 'none', sm: 'block' }, width: 8 }} />
                <TextField size="small" type="date" label="From" value={from} onChange={e => setFrom(e.target.value)}
                    InputLabelProps={{ shrink: true }} error={rangeBad} sx={{ ...fieldSx, width: { xs: 'calc(50% - 4px)', sm: 150 } }} />
                <TextField size="small" type="date" label="To" value={to} onChange={e => setTo(e.target.value)}
                    InputLabelProps={{ shrink: true }} error={rangeBad} sx={{ ...fieldSx, width: { xs: 'calc(50% - 4px)', sm: 150 } }} />
                <TextField size="small" select label="City" value={cityId} onChange={e => setCityId(e.target.value === '' ? '' : Number(e.target.value))}
                    InputLabelProps={{ shrink: true }} SelectProps={{ displayEmpty: true }} sx={{ ...fieldSx, width: { xs: '100%', sm: 160 } }}>
                    <MenuItem value="">All cities</MenuItem>
                    {cities.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
                </TextField>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, height: 36, border: `1px solid ${COLORS.border}`, borderRadius: '18px', px: 1.75, bgcolor: '#fff',
                    flex: { xs: '1 1 100%', sm: '0 1 280px' }, minWidth: 0 }}>
                    <SearchIcon sx={{ fontSize: 18, color: COLORS.muted }} />
                    <InputBase
                        value={q}
                        onChange={e => setQ(e.target.value)}
                        placeholder="Customer, email or attraction"
                        inputProps={{ 'aria-label': 'Search bookings' }}
                        sx={{ flex: 1, fontSize: 14, '& input': { border: '0 !important', outline: 0, boxShadow: 'none !important', bgcolor: 'transparent !important', p: '0 !important', height: 'auto' } }}
                    />
                </Box>
            </Box>
            {rangeBad && <Typography sx={{ fontSize: 12.5, color: '#B91C1C', mt: 1 }}>“From” is after “To”.</Typography>}

            {data && (
                <Typography data-bookings-count="1" sx={{ fontSize: 15, fontWeight: 700, color: COLORS.text, mt: 2.25 }}>
                    {countLine(data.total, when, filtered)}
                </Typography>
            )}

            <Box sx={{ height: 4, mt: 1 }}>{loading && <LinearProgress color="secondary" sx={{ borderRadius: 2 }} />}</Box>
            {failed && <Alert severity="error" sx={{ mb: 1.5, maxWidth: 760 }}>{failed}</Alert>}
            {!failed && !data && <CircularProgress size={24} />}

            {data && data.rows.length === 0 && !loading && (
                <Box data-bookings-empty="1" sx={{ mt: 0.5, bgcolor: '#fff', border: '1px dashed #D1D5DB', borderRadius: '12px', py: 6, px: 3, textAlign: 'center' }}>
                    <Typography sx={{ fontSize: 16, fontWeight: 700, color: COLORS.body }}>
                        {query ? `Nothing matches “${query}”` : filtered ? 'No bookings match these filters'
                            : when === 'upcoming' ? 'No upcoming bookings' : when === 'past' ? 'No past bookings' : 'No bookings yet'}
                    </Typography>
                    <Typography sx={{ fontSize: 13.5, color: COLORS.muted, mt: 0.75 }}>
                        A booking appears here when a customer answers “Yes” to “Did you book it?” in the app.
                    </Typography>
                </Box>
            )}

            {data && data.rows.length > 0 && (
                <Panel sx={{ mt: 0.5, overflow: 'hidden', opacity: loading ? 0.7 : 1, transition: 'opacity 120ms' }}>
                    <Box sx={{
                        display: { xs: 'none', md: 'grid' }, gridTemplateColumns: GRID, gap: 1.5, px: 2.25, py: 1, fontSize: 11.5, fontWeight: 700, color: COLORS.muted,
                        textTransform: 'uppercase', letterSpacing: '.4px', borderBottom: `1px solid ${COLORS.rowBorder}`,
                    }}>
                        <span>Date &amp; time</span><span>Attraction</span><span>Customer</span><span>Marked booked</span>
                        <span style={{ textAlign: 'end' }}>Viator</span>
                    </Box>
                    {data.rows.map(b => <BookingRow key={b.id} b={b} />)}
                    {pages > 1 && (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, px: 2.25, py: 1.25, borderTop: `1px solid ${COLORS.rowBorder}` }}>
                            <Typography sx={{ fontSize: 12.5, color: COLORS.muted }}>
                                {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, data.total)} of {fmtCount(data.total)}
                            </Typography>
                            <Pagination size="small" count={pages} page={page} onChange={(_, v) => setPage(v)} />
                        </Box>
                    )}
                </Panel>
            )}
        </AttractionsFrame>
    );
};

export default AttractionsBookingsPage;
